/**
 * ClientePicker — seletor de cliente (contato) para vincular a um projeto.
 *
 * Busca em Clientes (contatos com deal ganho — módulo Clientes) por nome/email/
 * empresa. `value` = { id, nome } | null; `onChange(cliente|null)` devolve a
 * escolha (ou null ao limpar). Reutilizável no criar-projeto e no board.
 */

import { useEffect, useRef, useState } from 'react'
import Icon from '../../components/icons.jsx'
import { getClientes } from '../../db/api.js'

export default function ClientePicker({ value, onChange, placeholder = 'Buscar cliente…' }) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const boxRef = useRef(null)

  // fecha ao clicar fora
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  // busca com debounce
  useEffect(() => {
    if (!open) return
    let alive = true
    setLoading(true)
    const t = setTimeout(async () => {
      try { const r = await getClientes(q.trim() || undefined); if (alive) setResults(r || []) }
      catch { if (alive) setResults([]) }
      finally { if (alive) setLoading(false) }
    }, 250)
    return () => { alive = false; clearTimeout(t) }
  }, [q, open])

  if (value) {
    return (
      <div style={S.selected}>
        <span style={S.avatar}>{(value.nome || '?').slice(0, 1).toUpperCase()}</span>
        <span style={S.selName}>{value.nome || value.id}</span>
        <button style={S.clear} onClick={() => onChange(null)} title="Desvincular">
          <Icon n="x" s={13} sw={2} />
        </button>
      </div>
    )
  }

  return (
    <div style={S.wrap} ref={boxRef}>
      <input style={S.input} value={q} placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQ(e.target.value); setOpen(true) }} />
      {open && (
        <div style={S.dropdown}>
          {loading && <div style={S.hint}>Buscando…</div>}
          {!loading && !results.length && <div style={S.hint}>Nenhum cliente encontrado.</div>}
          {results.map((c) => (
            <button key={c.id} style={S.opt}
              onClick={() => { onChange({ id: c.id, nome: c.nome }); setOpen(false); setQ('') }}>
              <span style={S.avatar}>{(c.nome || '?').slice(0, 1).toUpperCase()}</span>
              <div style={{ minWidth: 0, textAlign: 'left' }}>
                <div style={S.optName}>{c.nome || 'Sem nome'}</div>
                <div style={S.optMeta}>{[c.empresa, c.email].filter(Boolean).join(' · ') || '—'}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const S = {
  wrap: { position: 'relative' },
  input: {
    width: '100%', boxSizing: 'border-box', height: 38, padding: '0 12px', borderRadius: 8,
    border: '1px solid #d0d5dd', background: 'var(--surface)', color: '#101828', fontSize: 13, fontFamily: 'inherit',
  },
  dropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 6, zIndex: 60,
    background: 'var(--surface)', border: '1px solid #eaecf0', borderRadius: 12,
    boxShadow: '0 12px 32px rgba(16,24,40,.14)', maxHeight: 240, overflowY: 'auto', padding: 6,
  },
  opt: {
    display: 'flex', alignItems: 'center', gap: 8, width: '100%', border: 'none',
    background: 'transparent', cursor: 'pointer', padding: '8px 10px', borderRadius: 8, textAlign: 'left', fontFamily: 'inherit',
  },
  optName: { fontSize: 13, fontWeight: 600, color: '#101828', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  optMeta: { fontSize: 11.5, color: '#667085', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  hint: { fontSize: 12.5, color: '#98a2b3', padding: '8px 10px' },
  selected: {
    display: 'flex', alignItems: 'center', gap: 8, height: 38, padding: '0 10px', borderRadius: 8,
    border: '1px solid #d0d5dd', background: 'var(--surface)', boxSizing: 'border-box',
  },
  selName: { fontSize: 13, fontWeight: 600, color: '#344054', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  avatar: {
    width: 24, height: 24, borderRadius: '50%', background: '#101828', color: '#fff',
    fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  clear: { border: 'none', background: 'transparent', color: '#667085', cursor: 'pointer', padding: 2, display: 'flex' },
}
